import React from "react";
import styled from "styled-components";

import { FormOption } from "types/index";
import {
  ClientCreateResult,
  ClientRowStatus,
  ClientUploadRow,
  ValidatedClientRow,
} from "types/clientUpload";

import SelectInput from "components/Common/forms/SelectInput";

type ClientPreviewTableProps = {
  rows: ValidatedClientRow[];
  companyOptions: FormOption[];
  results: { [id: string]: ClientCreateResult };
  showPasswords: boolean;
  disabled: boolean;
  onChange: (id: string, patch: Partial<ClientUploadRow>) => void;
  onEmailBlur: (id: string) => void;
};

const statusLabels: { [status in ClientRowStatus]: string } = {
  ready: "Ready",
  warning: "Ready (check warnings)",
  error: "Will not be created",
  excluded: "Excluded",
};

const resultLabel = (result: ClientCreateResult) => {
  switch (result.state) {
    case "creating":
      return "Creating...";
    case "created":
      return "Created";
    case "exists":
      return "Already exists (not created)";
    case "profile-failed":
      return `Profile not linked (uid ${result.uid})`;
    default:
      return "Failed";
  }
};

// Rows that were created (or attempted and now exist) can no longer be edited
export const isDone = (result?: ClientCreateResult) =>
  !!result &&
  (result.state === "creating" ||
    result.state === "created" ||
    result.state === "exists" ||
    result.state === "profile-failed");

const ClientPreviewTable: React.FC<ClientPreviewTableProps> = ({
  rows,
  companyOptions,
  results,
  showPasswords,
  disabled,
  onChange,
  onEmailBlur,
}) => (
  <Fieldset disabled={disabled}>
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            <th>Include</th>
            <th>Row</th>
            <th>Date created</th>
            <th>Name</th>
            <th>Email</th>
            <th>Company</th>
            <th>Password</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const result = results[row.id];
            const locked = isDone(result);

            return (
              <tr key={row.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={row.include}
                    disabled={locked}
                    onChange={() => onChange(row.id, { include: !row.include })}
                  />
                </td>
                <td>{row.sheetRow}</td>
                <td>{row.dateCreated}</td>
                <td>
                  <Input
                    type="text"
                    value={row.name}
                    disabled={locked}
                    onChange={(e) => onChange(row.id, { name: e.target.value })}
                  />
                </td>
                <td>
                  <Input
                    type="text"
                    value={row.email}
                    disabled={locked}
                    onChange={(e) => onChange(row.id, { email: e.target.value })}
                    onBlur={() => onEmailBlur(row.id)}
                  />
                  {row.sheetEmail !== row.email && (
                    <SheetValue>sheet: "{row.sheetEmail}"</SheetValue>
                  )}
                </td>
                <td>
                  <SelectContainer>
                    {locked ? (
                      <span>
                        {
                          (
                            companyOptions.find(
                              (option) => option.value === row.companyId
                            ) || { text: row.sheetCompany }
                          ).text
                        }
                      </span>
                    ) : (
                      <SelectInput
                        name={`company-${row.id}`}
                        value={row.companyId || ""}
                        options={companyOptions}
                        onChange={(e) =>
                          onChange(row.id, {
                            companyId: e.target.value || null,
                            companyMatch: "manual",
                          })
                        }
                      />
                    )}
                  </SelectContainer>
                  <SheetValue>sheet: "{row.sheetCompany}"</SheetValue>
                </td>
                <td>
                  <Input
                    type={showPasswords ? "text" : "password"}
                    autoComplete="new-password"
                    value={row.password}
                    disabled={locked}
                    onChange={(e) =>
                      onChange(row.id, { password: e.target.value })
                    }
                  />
                </td>
                <td>
                  {result ? (
                    <>
                      <Badge state={result.state}>{resultLabel(result)}</Badge>
                      {"message" in result && (
                        <ResultMessage>{result.message}</ResultMessage>
                      )}
                    </>
                  ) : (
                    <Badge state={row.status}>{statusLabels[row.status]}</Badge>
                  )}
                  {!locked && row.issues.length > 0 && (
                    <IssueList>
                      {row.issues.map((issue, index) => (
                        <Issue key={index} severity={issue.severity}>
                          {issue.message}
                        </Issue>
                      ))}
                    </IssueList>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </TableWrapper>
  </Fieldset>
);

const Fieldset = styled.fieldset`
  border: 0;
  margin: 0;
  min-width: 0;
  padding: 0;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  table-layout: auto;
  border-collapse: collapse;
  margin-top: 1rem;
  width: 100%;
  font-size: 1.4rem;
  text-align: left;

  th,
  td {
    padding: 0.75rem;
    border: 1px solid black;
    vertical-align: top;
  }

  tr:nth-child(odd) td {
    background-color: ${(props) => props.theme.colors.grey};
  }
`;

const Input = styled.input`
  background-color: #fff;
  border: 1px solid ${(props) => props.theme.colors.darkGrey};
  font-size: 1.4rem;
  padding: 0.5rem;
  min-width: 16rem;
  width: 100%;
`;

const SelectContainer = styled.div`
  select {
    background-color: #fff;
    border: 1px solid ${(props) => props.theme.colors.darkGrey};
    border-radius: 0;
    font-size: 1.4rem;
    height: auto;
    min-width: 18rem;
    padding: 0.5rem;
  }
`;

const SheetValue = styled.small`
  color: ${(props) => props.theme.colors.idataDarkGrey};
  display: block;
  margin-top: 0.25rem;
  white-space: pre;
`;

const badgeColor = (state: string, colors: any) => {
  switch (state) {
    case "ready":
    case "created":
      return colors.green;
    case "warning":
    case "creating":
      return colors.yellow;
    case "excluded":
    case "exists":
      return colors.darkGrey;
    default:
      return colors.red;
  }
};

const Badge = styled.span<{ state: string }>`
  background-color: ${(props) => badgeColor(props.state, props.theme.colors)};
  border-radius: 0.5rem;
  color: #000;
  display: inline-block;
  font-weight: 700;
  padding: 0.25rem 0.75rem;
  white-space: nowrap;
`;

const IssueList = styled.ul`
  margin: 0.5rem 0 0 0;
  padding: 0;
  list-style: none;
`;

const Issue = styled.li<{ severity?: "error" | "warning" }>`
  color: ${(props) =>
    props.severity === "warning"
      ? props.theme.colors.orange
      : props.theme.colors.red};
  margin-top: 0.25rem;
`;

const ResultMessage = styled.p`
  color: ${(props) => props.theme.colors.red};
  margin: 0.5rem 0 0 0;
`;

export default ClientPreviewTable;
